import React from "react";
import type WidgetSubScoreDto from "../../../types/dtos/content-dashboard/WidgetSubScoreDto";
import { dateHelper } from "../../../helpers";
import { FormattedDate, Tooltip } from "../../common";

interface SubScoresBreakdownProps {
  subScores: WidgetSubScoreDto[];
  color: string; // Widget color to use for all bars
  onToggle: () => void;
}

const SubScoresBreakdown: React.FC<SubScoresBreakdownProps> = ({
  subScores,
  color,
  onToggle,
}) => {
  // Format sub-score display value
  const formatSubScore = (subScore: WidgetSubScoreDto) => {
    if (isNaN(Number(subScore.displayValue))) {
      return subScore.displayValue;
    }
    if (
      subScore.scaleScore !== null &&
      subScore.scaleScore !== undefined &&
      subScore.scaleMax !== null
    ) {
      // Use scale score
      const isInteger = subScore.scaleScore === Math.floor(subScore.scaleScore);
      const formatted = isInteger
        ? subScore.scaleScore.toFixed(0)
        : subScore.scaleScore.toFixed(1);
      return `${formatted} / ${subScore.scaleMax}`;
    }
    // Use percentage
    return `${Number(subScore.displayValue).toFixed(0)}%`;
  };

  // Calculate bar width percentage
  const getBarWidth = (subScore: WidgetSubScoreDto) => {
    if (isNaN(Number(subScore.displayValue))) {
      return 0;
    }

    if (
      subScore.scaleScore !== null &&
      subScore.scaleScore !== undefined &&
      subScore.scaleMax !== null &&
      subScore.scaleMax !== undefined &&
      subScore.scaleMax > 0
    ) {
      return (subScore.scaleScore / subScore.scaleMax) * 100;
    }
    return Number(subScore.displayValue);
  };

  if (!subScores || subScores.length === 0) return null;

  return (
    <div className="h-full flex flex-col">
      {/* Breakdown Content */}
      <div className="flex-1 overflow-auto px-4 pb-4 pt-2 min-h-0">
        <div className="space-y-3">
          {subScores.map((subScore, index) => (
            <div key={subScore.dataPointId}>
              {/* Sub-score name and value */}
              <div className="flex justify-between items-center mb-1.5">
                <span className="text-xs font-medium text-gray-700">
                  {subScore.displayName}
                  <SubScoreSourceInfo
                    sourceJourneyTitle={subScore.sourceJourneyTitle}
                    scoreDate={subScore.scoreDate}
                  />
                </span>
                <span className="text-xs font-semibold text-gray-900">
                  {formatSubScore(subScore)}
                </span>
              </div>
              {/* Horizontal bar */}
              <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full transition-all duration-500 ease-out"
                  style={{
                    width: `${Math.min(
                      100,
                      Math.max(0, getBarWidth(subScore)),
                    )}%`,
                    backgroundColor: color,
                    transitionDelay: `${index * 50}ms`,
                  }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Toggle Button at bottom */}
      <div className="flex-shrink-0 border-t border-gray-200">
        <button
          onClick={onToggle}
          className="w-full flex items-center justify-center gap-2 px-3 py-2 transition-all duration-200 group"
        >
          <span className="text-sm font-medium text-gray-700 group-hover:text-gray-900">
            Hide breakdown
          </span>
        </button>
      </div>
    </div>
  );
};

const SubScoreSourceInfo = ({
  sourceJourneyTitle,
  scoreDate,
}: {
  sourceJourneyTitle: string | null | undefined;
  scoreDate: Date | null | undefined;
}) => {
  if (!sourceJourneyTitle && !scoreDate) return null;

  const scoreDateObj = scoreDate
    ? dateHelper.convertUtcDateToLocal(scoreDate)
    : null;

  let bodyContent = null;
  if (sourceJourneyTitle && scoreDateObj) {
    /* Title and date */
    bodyContent = (
      <>
        <Tooltip
          htmlContent={
            <span>
              <FormattedDate date={scoreDateObj} displayMode="DATE-ONLY" />
            </span>
          }
          triggerElement={<span>{sourceJourneyTitle}</span>}
          content={""}
        />
      </>
    );
  } else if (sourceJourneyTitle) {
    /* Title only */
    bodyContent = (
      <>
        <span>{sourceJourneyTitle}</span>
      </>
    );
  } else if (scoreDateObj) {
    /* Date only */
    bodyContent = (
      <>
        <FormattedDate date={scoreDateObj} displayMode="DATE-ONLY" />
      </>
    );
  }

  if (!bodyContent) return null;

  return (
    <div className="flex items-center gap-2">
      <span className="text-xs font-medium text-gray-500 pl-2">
        {bodyContent}
      </span>
    </div>
  );
};

export default SubScoresBreakdown;
